import React, { useState, useEffect } from 'react';
import { Spin, Empty, Segmented } from 'antd';
import {
  TrendingUp, TrendingDown, IndianRupee, Users, Ticket, Wallet, Target, Sparkles,
} from 'lucide-react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  Filler, Tooltip, Legend,
} from 'chart.js';
import useApiMethods from '../../lib/useApiMethods';
import { TRENDS } from '../../constants/api';
import { MetricCard, HeadlineCard, Surface } from '../../components/insights';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const formatLakhs = (v) => `₹${((v || 0) / 100000).toFixed(1)}L`;
const formatNumber = (v) => new Intl.NumberFormat('en-IN').format(Math.round(v || 0));
const formatPct = (v) => `${(v || 0).toFixed(1)}%`;

// Intent mapping from group color → MetricCard intent
const GROUP_INTENT = {
  emerald: 'positive',
  rose: 'negative',
  indigo: 'primary',
  violet: 'primary',
  amber: 'warning',
};

// Metric definitions — each group has a color + metrics
const METRIC_GROUPS = [
  {
    title: 'Sales & Revenue',
    icon: IndianRupee,
    color: 'emerald',
    description: 'Leads, pipeline, bookings, cash, and recognized revenue',
    metrics: [
      { key: 'new_leads_today', label: 'New Leads Today', fmt: formatNumber, goodUp: true },
      { key: 'new_leads_this_week', label: 'New Leads Wk', fmt: formatNumber, goodUp: true },
      { key: 'new_leads_this_month', label: 'New Leads Mo', fmt: formatNumber, goodUp: true },
      { key: 'bookings_this_month', label: 'Bookings Mo', fmt: formatLakhs, goodUp: true },
      { key: 'cash_collected_this_month', label: 'Cash Collected Mo', fmt: formatLakhs, goodUp: true },
      { key: 'crm_cash_this_month', label: 'CRM Cash Mo', fmt: formatLakhs, goodUp: true },
      { key: 'manual_cash_this_month', label: 'Manual Cash Mo', fmt: formatLakhs, goodUp: true },
      { key: 'recognized_revenue_this_month', label: 'Recognized Rev Mo', fmt: formatLakhs, goodUp: true },
      { key: 'pipeline_total', label: 'Pipeline Total', fmt: formatLakhs, goodUp: true },
      { key: 'win_rate', label: 'Win Rate', fmt: formatPct, goodUp: true },
      { key: 'deals_closed_this_week', label: 'Deals Closed Wk', fmt: formatNumber, goodUp: true },
    ],
  },
  {
    title: 'Receivables',
    icon: Wallet,
    color: 'rose',
    description: 'Outstanding client billing and AMC health',
    metrics: [
      { key: 'outstanding_receivables', label: 'Outstanding', fmt: formatLakhs, goodUp: false },
      { key: 'clients_with_pending', label: 'Clients Pending', fmt: formatNumber, goodUp: false },
      { key: 'amc_due_this_month', label: 'AMC Due This Month', fmt: formatLakhs, goodUp: false },
      { key: 'amc_overdue_count', label: 'AMC Overdue', fmt: formatNumber, goodUp: false },
    ],
  },
  {
    title: 'HR',
    icon: Users,
    color: 'indigo',
    description: 'Headcount, attendance, payroll, and retention signals',
    metrics: [
      { key: 'headcount', label: 'Headcount', fmt: formatNumber, goodUp: true },
      { key: 'attrition_rate', label: 'Attrition Rate', fmt: formatPct, goodUp: false },
      { key: 'new_hires_this_month', label: 'New Hires Mo', fmt: formatNumber, goodUp: true },
      { key: 'exits_this_month', label: 'Exits Mo', fmt: formatNumber, goodUp: false },
      { key: 'open_positions', label: 'Open Positions', fmt: formatNumber, goodUp: true },
      { key: 'pending_leaves', label: 'Pending Leaves', fmt: formatNumber, goodUp: false },
      { key: 'reimb_pending_count', label: 'Reimb. Pending', fmt: formatNumber, goodUp: false },
      { key: 'reimb_paid_this_month_amount', label: 'Reimb. Paid Mo', fmt: formatLakhs, goodUp: true },
      { key: 'advance_pending_count', label: 'Adv. Pending', fmt: formatNumber, goodUp: false },
      { key: 'advance_disbursed_this_month_amount', label: 'Adv. Disbursed Mo', fmt: formatLakhs, goodUp: true },
    ],
  },
  {
    title: 'Development',
    icon: Ticket,
    color: 'violet',
    description: 'Backlog, sprint velocity, and production quality',
    metrics: [
      { key: 'open_tickets', label: 'Open Tickets', fmt: formatNumber, goodUp: false },
      { key: 'production_bugs', label: 'Production Bugs', fmt: formatNumber, goodUp: false },
      { key: 'active_sprints', label: 'Active Sprints', fmt: formatNumber, goodUp: true },
      { key: 'avg_sprint_completion', label: 'Sprint Completion', fmt: formatPct, goodUp: true },
      { key: 'cr_pending', label: 'Client Reqs Pending', fmt: formatNumber, goodUp: false },
    ],
  },
  {
    title: 'Finance',
    icon: Target,
    color: 'amber',
    description: 'Allocated budget, spend, utilization, and outstanding receivables',
    metrics: [
      { key: 'total_budget_allocated', label: 'Budget Allocated', fmt: formatLakhs, goodUp: true },
      { key: 'total_actuals_this_month', label: 'Total Spend (Mo)', fmt: formatLakhs, goodUp: false },
      { key: 'budget_utilization', label: 'Budget Utilization', fmt: formatPct, goodUp: false },
      { key: 'outstanding_invoices', label: 'Outstanding Invoices', fmt: formatNumber, goodUp: false },
      { key: 'cash_balance', label: 'Cash Balance', fmt: formatLakhs, goodUp: true },
    ],
  },
];

const GROUP_RING_COLORS = {
  emerald: 'ring-emerald-500/20 dark:ring-emerald-500/30 bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400',
  rose: 'ring-rose-500/20 dark:ring-rose-500/30 bg-rose-100 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400',
  indigo: 'ring-indigo-500/20 dark:ring-indigo-500/30 bg-indigo-100 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400',
  violet: 'ring-violet-500/20 dark:ring-violet-500/30 bg-violet-100 dark:bg-violet-950/40 text-violet-600 dark:text-violet-400',
  amber: 'ring-amber-500/20 dark:ring-amber-500/30 bg-amber-100 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400',
};

const GROUP_LINE_COLORS = {
  emerald: ['#10b981', '#34d399'],
  rose: ['#f43f5e', '#fb7185'],
  indigo: ['#6366f1', '#818cf8'],
  violet: ['#8b5cf6', '#a78bfa'],
  amber: ['#f59e0b', '#fbbf24'],
};

// Find the biggest-movers insight for a group
const buildGroupInsight = (group, deltas) => {
  const movers = group.metrics
    .map((m) => {
      const d = deltas[m.key] || {};
      const pct = d.delta_pct;
      if (pct == null || Math.abs(pct) < 0.5) return null;
      const good = m.goodUp ? pct > 0 : pct < 0;
      return { ...m, pct, good, current: d.current, previous: d.previous };
    })
    .filter(Boolean)
    .sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct));

  if (movers.length === 0) return null;
  const top = movers[0];
  return {
    top,
    totalMovers: movers.length,
    goodCount: movers.filter((m) => m.good).length,
  };
};

const Trends = () => {
  const { fetch } = useApiMethods();
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await fetch(TRENDS, { period });
      if (!cancelled && res?.status === 'success') setData(res.data);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spin size="large" />
      </div>
    );
  }

  const deltas = data?.deltas || {};
  const timeSeries = data?.time_series || [];
  const hasPrev = data?.has_previous;
  const snapshotsAvailable = data?.snapshots_available || 0;

  const periodLabel = period === 'week' ? 'Last 7 Days' : period === 'month' ? 'Last 30 Days' : 'Last 365 Days';
  const compareLabel = period === 'week' ? 'vs last week' : period === 'month' ? 'vs last month' : 'vs last year';

  // Overall summary — count biggest movers across all groups
  const allMovers = Object.entries(deltas)
    .filter(([, d]) => d?.delta_pct != null && Math.abs(d.delta_pct) > 0.5);

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen text-foreground">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Trends</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {periodLabel} · {snapshotsAvailable} daily snapshots · {compareLabel}
          </p>
        </div>
        <Segmented
          value={period}
          onChange={setPeriod}
          options={[
            { label: 'Week', value: 'week' },
            { label: 'Month', value: 'month' },
            { label: 'Year', value: 'year' },
          ]}
        />
      </div>

      {/* Headline */}
      {!hasPrev ? (
        <HeadlineCard
          state="info"
          title="Building your trend history"
          description={`Daily snapshots started ${snapshotsAvailable > 0 ? `${snapshotsAvailable} ${snapshotsAvailable === 1 ? 'day' : 'days'} ago` : 'today'}. ${
            period === 'week'
              ? 'Week-over-week comparison activates after 7 daily snapshots.'
              : period === 'month'
                ? 'Month-over-month comparison activates after 30 daily snapshots.'
                : 'Year-over-year comparison activates after 365 daily snapshots.'
          } Current values are shown below; deltas appear as history accumulates.`}
        />
      ) : (
        <HeadlineCard
          state={allMovers.length > 10 ? 'warning' : 'healthy'}
          title={`${allMovers.length} metric${allMovers.length === 1 ? '' : 's'} moved ${compareLabel}`}
          description={`Compared against ${data?.previous_date}. Scroll below for per-department breakdown — click any metric to see the full daily series.`}
          metrics={[
            { label: 'Snapshots', value: snapshotsAvailable },
            { label: 'Period', value: periodLabel },
            { label: 'Baseline', value: data?.previous_date || '—' },
            { label: 'Today', value: data?.current_date || '—' },
          ]}
        />
      )}

      {/* Metric groups */}
      {METRIC_GROUPS.map((group) => {
        const Icon = group.icon;
        const insight = buildGroupInsight(group, deltas);
        const intent = GROUP_INTENT[group.color] || 'neutral';
        const ringClasses = GROUP_RING_COLORS[group.color] || GROUP_RING_COLORS.emerald;
        const lineColors = GROUP_LINE_COLORS[group.color] || GROUP_LINE_COLORS.emerald;

        return (
          <Surface key={group.title} padding="md">
            {/* Group header */}
            <div className="flex items-start gap-3 mb-4">
              <span className={`w-10 h-10 rounded-xl flex items-center justify-center ring-1 ${ringClasses}`}>
                <Icon className="w-5 h-5" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <h3 className="font-semibold text-foreground">{group.title}</h3>
                  <span className="text-xs text-muted-foreground">{group.description}</span>
                </div>
                {insight && (
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    <Sparkles className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-muted-foreground">Biggest move:</span>
                    <span className="font-semibold text-foreground">{insight.top.label}</span>
                    <span className={`font-semibold ${insight.top.good ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                      {insight.top.pct > 0 ? '+' : ''}{insight.top.pct.toFixed(1)}%
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Metric cards grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {group.metrics.map((m) => {
                const d = deltas[m.key] || {};
                const series = timeSeries.length > 0
                  ? timeSeries.map((t) => t[m.key] || 0)
                  : null;
                return (
                  <MetricCard
                    key={m.key}
                    title={m.label}
                    value={m.fmt(d.current)}
                    subtitle={hasPrev ? `was ${m.fmt(d.previous)}` : undefined}
                    intent={intent}
                    delta={d.delta_pct}
                    goodUp={m.goodUp}
                    sparkline={series}
                  />
                );
              })}
            </div>

            {/* Time series chart */}
            {timeSeries.length >= 2 && (
              <div className="mt-5 pt-5 border-t border-border">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Trend · top 2 metrics
                </div>
                <div className="h-48">
                  <Line
                    data={{
                      labels: timeSeries.map((t) => t.date.slice(5)),
                      datasets: group.metrics.slice(0, 2).map((m, idx) => ({
                        label: m.label,
                        data: timeSeries.map((t) => t[m.key] || 0),
                        borderColor: lineColors[idx] || lineColors[0],
                        backgroundColor: `${lineColors[idx] || lineColors[0]}14`,
                        borderWidth: 2,
                        pointRadius: 3,
                        pointBackgroundColor: lineColors[idx] || lineColors[0],
                        tension: 0.35,
                        fill: idx === 0,
                      })),
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: { boxWidth: 10, font: { size: 11 }, color: 'rgba(148,163,184,0.9)' },
                        },
                      },
                      scales: {
                        x: { grid: { display: false }, ticks: { color: 'rgba(148,163,184,0.9)' } },
                        y: { grid: { color: 'rgba(148,163,184,0.15)' }, ticks: { color: 'rgba(148,163,184,0.9)' } },
                      },
                    }}
                  />
                </div>
              </div>
            )}
          </Surface>
        );
      })}

      {timeSeries.length === 0 && (
        <Empty description="No snapshots yet — they'll start accumulating from today." />
      )}
    </div>
  );
};

export default Trends;
